/**
 * GalleryV2Layout
 *
 * Hauptlayout der Gallery V2 mit Content links und Sidebar rechts.
 * Desktop: Sidebar als feste Spalte rechts.
 * Mobile: Sidebar als Bottom-Sheet.
 */

import { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PanelRight } from 'lucide-react';
import { useGallerySearch } from '@/hooks/useGalleryV2';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { GalleryV2Sidebar } from './GalleryV2Sidebar';
import { GalleryV2Content } from './GalleryV2Content';
import { GalleryMobileSheet } from './GalleryMobileSheet';
import { ImageDetailModal, FloatingApprovalToggle } from '@/components/v2/tools/gallery';
import { galleryImageToLegacy } from '@/lib/api/gallery-v2';
import type { GalleryImage } from '@/lib/api/gallery-v2';

export type ViewMode = 'grid' | 'grid5' | 'list' | 'bento';

export function GalleryV2Layout() {
  const { t } = useTranslation();
  const { params, updateParams, approvalModeEnabled, images } = useGallerySearch();
  const isDesktop = useMediaQuery('(min-width: 768px)');

  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeImage, setActiveImage] = useState<GalleryImage | null>(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const isApprovalMode =
    approvalModeEnabled &&
    params.approval_status?.length === 1 &&
    params.approval_status[0] === 'pending';

  const handleToggleApprovalMode = useCallback(() => {
    const current =
      params.approval_status?.length === 1 && params.approval_status[0] === 'pending';
    updateParams({ approval_status: current ? ['pending', 'approved'] : ['pending'] });
  }, [params.approval_status, updateParams]);

  const legacyImage = useMemo(
    () => (activeImage ? galleryImageToLegacy(activeImage) : null),
    [activeImage]
  );

  const handleOpenLightbox = useCallback(() => {
    if (activeImage) setLightboxOpen(true);
  }, [activeImage]);

  const handleImageClick = useCallback((image: GalleryImage) => {
    setActiveImage(image);
    setLightboxOpen(true);
  }, []);

  const idx = activeImage && images ? images.findIndex((img) => img.id === activeImage.id) : -1;
  const handleNavigatePrev = useCallback(() => {
    if (!activeImage || !images) return;
    const i = images.findIndex((img) => img.id === activeImage.id);
    if (i > 0) setActiveImage(images[i - 1]);
  }, [activeImage, images]);
  const handleNavigateNext = useCallback(() => {
    if (!activeImage || !images) return;
    const i = images.findIndex((img) => img.id === activeImage.id);
    if (i >= 0 && i < images.length - 1) setActiveImage(images[i + 1]);
  }, [activeImage, images]);

  return (
    <div className="flex h-full overflow-hidden">
      {/* Main Content */}
      <main className="flex-1 flex flex-col overflow-hidden">
        <GalleryV2Content
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
          sidebarOpen={sidebarOpen}
          onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
          onImageClick={handleImageClick}
          isApprovalMode={isApprovalMode}
        />
      </main>

      {/* Desktop: Pull-Tab when sidebar closed */}
      {isDesktop && !sidebarOpen && (
        <button
          onClick={() => setSidebarOpen(true)}
          title={t('gallery.sidebar.openSidebar')}
          aria-label={t('gallery.sidebar.openSidebar')}
          className="flex-shrink-0 w-5 self-stretch flex items-center justify-center border-l border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors cursor-pointer"
        >
          <PanelRight className="h-4 w-4" />
        </button>
      )}

      {/* Desktop: Sidebar */}
      {isDesktop && sidebarOpen && (
        <aside className="w-80 flex-shrink-0 border-l border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 overflow-y-auto">
          <GalleryV2Sidebar
            onClose={() => setSidebarOpen(false)}
            activeImage={activeImage}
            onOpenLightbox={handleOpenLightbox}
          />
        </aside>
      )}

      {/* Mobile: Bottom Sheet */}
      {!isDesktop && (
        <GalleryMobileSheet
          open={sidebarOpen}
          onOpenChange={setSidebarOpen}
          activeImage={activeImage}
          onOpenLightbox={handleOpenLightbox}
        />
      )}

      {/* Approval Toggle */}
      {approvalModeEnabled && (
        <FloatingApprovalToggle
          isApprovalMode={isApprovalMode}
          onToggle={handleToggleApprovalMode}
        />
      )}

      {/* Lightbox Modal */}
      <ImageDetailModal
        image={legacyImage}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        onNavigatePrev={idx > 0 ? handleNavigatePrev : undefined}
        onNavigateNext={idx >= 0 && images && idx < images.length - 1 ? handleNavigateNext : undefined}
      />
    </div>
  );
}
